// Send engagement schedules to everyone on an event who hasn't been sent one yet
// (Contracts.personnel). Tick who; each is merged from the template in turn (one
// request each, with progress), then one Shelter Stone signature is applied to
// them all and each person is emailed. Anyone already sent one — or signed — is
// left out, so the same button picks up late additions after the first round;
// a senior role sent early from their own attendance is simply not offered again.
import { useEffect, useState } from "react";
import axios from "axios";
import {
  Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControlLabel, LinearProgress, Stack, Typography,
} from "@mui/material";
import ShelterStoneSignDialog from "./ShelterStoneSignDialog";
import { CONTRACTABLE_ATTENDANCE_STATUSES, CONTRACTS_API } from "../contracts";
import { useFullScreenDialog } from "../useFullScreenDialog";

const SENT = ["sent", "signed", "completed"];
const nameOf = (a) => (a.person ? `${a.person.forename} ${a.person.surname}` : `#${a.personnel_id}`);

/** Props: open, event, attendances (the event's rows, already sorted), onClose, onDone. */
export default function BulkContractDialog({ open, event, attendances, onClose, onDone }) {
  const fullScreen = useFullScreenDialog();
  const [loading, setLoading] = useState(true);
  const [hasTemplate, setHasTemplate] = useState(false);
  const [sentIds, setSentIds] = useState(new Set());
  const [chosen, setChosen] = useState(new Set());
  const [progress, setProgress] = useState(null);   // {done, total}
  const [failures, setFailures] = useState([]);     // [{name, error}]
  const [prepared, setPrepared] = useState(null);   // contracts ready to sign

  useEffect(() => {
    if (!open || !event?.id) return;
    setLoading(true); setProgress(null); setFailures([]); setPrepared(null);
    Promise.all([
      axios.get(CONTRACTS_API + "/", { params: { event_id: event.id, kind: "personnel" } }),
      axios.get(`${CONTRACTS_API}/engagement/${event.id}`),
    ]).then(([c, e]) => {
      const sent = new Set((c.data || []).filter(k => SENT.includes(k.status)).map(k => k.attendance_id));
      setSentIds(sent);
      setHasTemplate(!!e.data.template_filename);
      setChosen(new Set(attendances
        .filter(a => CONTRACTABLE_ATTENDANCE_STATUSES.includes(a.status) && !sent.has(a.id))
        .map(a => a.id)));
    }).catch(() => {}).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, event?.id]);

  const candidates = attendances.filter(a => CONTRACTABLE_ATTENDANCE_STATUSES.includes(a.status) && !sentIds.has(a.id));
  const busy = progress !== null && prepared === null;

  const toggle = (id) => setChosen(s => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const prepareAll = async () => {
    const list = candidates.filter(a => chosen.has(a.id));
    const ok = [], failed = [];
    setProgress({ done: 0, total: list.length });
    for (const a of list) {
      try {
        const res = await axios.post(CONTRACTS_API + "/personnel/prepare", { attendance_id: a.id },
                                     { suppressErrorToast: true });
        ok.push(res.data.contract);
        if (res.data.blank_fields.length) {
          failed.push({ name: nameOf(a), error: `left blank: ${res.data.blank_fields.join(", ")}`, warn: true });
        }
      } catch (err) {
        const d = err?.response?.data?.detail;
        failed.push({ name: nameOf(a), error: typeof d === "string" ? d : "couldn't be prepared" });
      }
      setProgress({ done: ok.length + failed.filter(f => !f.warn).length, total: list.length });
    }
    setFailures(failed);
    setPrepared(ok);
  };

  return (
    <>
      <Dialog open={open && !(prepared && prepared.length)} onClose={busy ? undefined : onClose}
              fullWidth maxWidth="sm" fullScreen={fullScreen}>
        <DialogTitle>Send engagement schedules — {event?.label_long || event?.name}</DialogTitle>
        <DialogContent dividers>
          {loading ? (
            <Stack sx={{ alignItems: "center", py: 3 }}><CircularProgress /></Stack>
          ) : !hasTemplate ? (
            <Alert severity="info">
              This event has no engagement schedule template yet. Add one on the event's
              Engagement schedules section first.
            </Alert>
          ) : candidates.length === 0 ? (
            <Typography color="text.secondary">
              Everyone at Selected or Contract Sent has already been sent an engagement schedule.
            </Typography>
          ) : (
            <Stack spacing={1}>
              <Typography variant="body2" color="text.secondary">
                Everyone at Selected or Contract Sent who hasn't been sent one yet. Each is merged
                from the template with their own details; you sign once for all of them.
              </Typography>
              <FormControlLabel
                control={<Checkbox checked={chosen.size === candidates.length}
                                   indeterminate={chosen.size > 0 && chosen.size < candidates.length}
                                   onChange={e => setChosen(new Set(e.target.checked ? candidates.map(a => a.id) : []))} />}
                label={<Typography variant="body2" sx={{ fontWeight: 600 }}>All ({candidates.length})</Typography>} />
              {candidates.map(a => (
                <FormControlLabel key={a.id} sx={{ ml: 1 }}
                  control={<Checkbox size="small" checked={chosen.has(a.id)} onChange={() => toggle(a.id)} disabled={busy} />}
                  label={(
                    <Box>
                      <Typography variant="body2">{nameOf(a)}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {[a.role || "No event role", a.status, a.fee == null ? "no fee set" : null].filter(Boolean).join(" · ")}
                      </Typography>
                    </Box>
                  )} />
              ))}
              {busy && (
                <Box>
                  <LinearProgress variant="determinate" value={(100 * progress.done) / Math.max(progress.total, 1)} />
                  <Typography variant="caption" color="text.secondary">
                    Preparing {progress.done} of {progress.total}…
                  </Typography>
                </Box>
              )}
              {prepared && prepared.length === 0 && (
                <Alert severity="error">Nothing could be prepared.</Alert>
              )}
              {failures.length > 0 && (
                <Alert severity="warning">
                  {failures.map((f, i) => <div key={i}>{f.name}: {f.error}</div>)}
                </Alert>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={busy}>Cancel</Button>
          {hasTemplate && candidates.length > 0 && (
            <Button variant="contained" disabled={chosen.size === 0 || busy} onClick={prepareAll}>
              Prepare {chosen.size} and sign
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <ShelterStoneSignDialog open={open && !!(prepared && prepared.length)} contracts={prepared || []}
                              recipientLabel="person"
                              notes={failures.map(f => `${f.name}: ${f.error}`)}
                              onClose={() => { setPrepared(null); onDone?.(); onClose(); }}
                              onDone={() => onDone?.()} />
    </>
  );
}
